/**
 * Effort Formatting Utilities
 * Shared functions for displaying estimated effort across the application.
 */

/**
 * Format estimated effort for display.
 * If total minutes > 90, displays in hours. Otherwise displays in original unit.
 * 
 * @param {string|number} effort - The effort value
 * @param {string} metric - The unit ('minutes', 'hours', 'days')
 * @returns {string} Formatted string like "2 hours" or "45 minutes"
 */
export const formatEstimatedEffort = (effort, metric = 'hours') => {
  if (!effort && effort !== 0) return '';
  
  const value = parseFloat(effort);
  if (isNaN(value)) return '';
  
  // Convert to minutes first
  let totalMinutes;
  const unit = (metric || 'hours').toLowerCase();
  
  switch (unit) {
    case 'minutes':
    case 'minute':
    case 'min':
    case 'mins':
      totalMinutes = value;
      break;
    case 'hours':
    case 'hour':
    case 'hr':
    case 'hrs':
      totalMinutes = value * 60;
      break;
    case 'days':
    case 'day':
    case 'd':
      totalMinutes = value * 480; // 8 work hours per day
      break;
    default:
      // If unit not recognized, assume the value is already in the display format
      totalMinutes = value * 60; // Default to treating as hours
  }
  
  // If more than 90 minutes, display in hours
  if (totalMinutes > 90) {
    const hours = totalMinutes / 60;
    // Round to 1 decimal place if needed
    const displayHours = hours % 1 === 0 ? hours : Math.round(hours * 10) / 10;
    return `${displayHours} ${displayHours === 1 ? 'hour' : 'hours'}`;
  }
  
  // Otherwise display in minutes
  const displayMinutes = Math.round(totalMinutes);
  return `${displayMinutes} ${displayMinutes === 1 ? 'minute' : 'minutes'}`;
};

/**
 * Format effort from minutes (normalized storage format).
 * If > 90 minutes, displays in hours.
 * 
 * @param {number} minutes - Total minutes
 * @returns {string} Formatted string
 */
export const formatEffortFromMinutes = (minutes) => {
  if (!minutes && minutes !== 0) return '';
  
  const totalMinutes = parseInt(minutes, 10);
  if (isNaN(totalMinutes)) return '';
  
  // If more than 90 minutes, display in hours
  if (totalMinutes > 90) {
    const hours = totalMinutes / 60;
    const displayHours = hours % 1 === 0 ? hours : Math.round(hours * 10) / 10;
    return `${displayHours} ${displayHours === 1 ? 'hour' : 'hours'}`;
  }
  
  // Otherwise display in minutes
  return `${totalMinutes} ${totalMinutes === 1 ? 'minute' : 'minutes'}`;
};

/**
 * Convert effort value and unit to minutes.
 * 
 * @param {number} value - Effort value
 * @param {string} unit - Unit ('minutes', 'hours', 'days')
 * @returns {number} Total minutes
 */
export const effortToMinutes = (value, unit) => {
  if (!value) return 0;
  
  const numValue = parseFloat(value);
  if (isNaN(numValue)) return 0;
  
  switch ((unit || 'hours').toLowerCase()) {
    case 'minutes':
    case 'minute':
    case 'min':
      return numValue;
    case 'hours':
    case 'hour':
    case 'hr':
      return numValue * 60;
    case 'days':
    case 'day':
      return numValue * 480;
    default:
      return numValue * 60; // Default to hours
  }
};

/**
 * Convert minutes to cleanest value + unit representation.
 * 
 * @param {number} minutes - Total minutes
 * @returns {{value: number, unit: string}} Object with value and unit
 */
export const minutesToEffort = (minutes) => {
  if (!minutes) return { value: null, unit: 'hours' };
  
  const totalMinutes = parseInt(minutes, 10);
  
  // Days (480 min = 8 hours) - only if evenly divisible
  if (totalMinutes >= 480 && totalMinutes % 480 === 0) {
    return { value: totalMinutes / 480, unit: 'days' };
  }
  
  // Hours - only if evenly divisible
  if (totalMinutes >= 60 && totalMinutes % 60 === 0) {
    return { value: totalMinutes / 60, unit: 'hours' };
  }
  
  // Default to minutes
  return { value: totalMinutes, unit: 'minutes' };
};
